import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import {
  FiBarChart2,
  FiBriefcase,
  FiUserPlus,
  FiMail,
  FiLogOut,
  FiCreditCard,
  FiClock,
  FiSettings,
  FiCompass,
  FiLock,
} from "react-icons/fi";
import { getStartedDone, openOnPlan, useGetStarted } from "../lib/getStartedService";
import { DeviceSearch } from "./ui/icons";
import { useAuth } from "../context/AuthContext";
import { formatCredits, useCredits } from "../utils/useCredits";
import { isInhouse, usePlan } from "../utils/usePlan";
import { useFindNotification } from "../context/FindNotificationContext";
import {
  getCachedUserSettings,
  getUserSettings,
  subscribeUserSettings,
} from "../lib/userSettingsService";
import Skeleton from "./Skeleton";
import { useLocation } from "react-router-dom";
import {
  Sidebar as SidebarRoot,
  SidebarBody,
  SidebarLink,
  SidebarAction,
  useSidebar,
} from "./ui/sidebar";
import { publicAsset } from "../utils/publicAsset";

const Shell = styled.div`
  height: 100dvh;
  max-height: 100dvh;
  margin: 0;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1001;
  overflow: hidden;
  width: auto;

  @media (max-width: 1024px) {
    margin: 0;
    left: 0;
    top: 0;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
    width: 100%;
    /* Avoid a transparent full-screen layer blocking taps on mobile. */
    pointer-events: none;
  }
`;

const DesktopInner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 0.75rem;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.15rem 0rem;
  margin-top: -6px;
`;

// First whitespace-separated token of a display name ("Ada Lovelace" → "Ada").
const firstWord = (name) => String(name || "").trim().split(/\s+/)[0] || "";

/* One lockup image for both rail states. The leading icon mark occupies the
   first 400px of the 1574x403 source, so at LOGO_H it renders exactly
   LOGO_ICON_W wide — clipping to that width leaves the icon perfectly still
   while the wordmark slides out of view. */
const LOGO_H = 26;
const LOGO_W = 102;
const LOGO_ICON_W = 26;

const LogoClip = styled.div`
  height: ${LOGO_H}px;
  width: ${({ $visible }) => ($visible ? LOGO_W : LOGO_ICON_W)}px;
  overflow: hidden;
  flex-shrink: 0;
  transition: width 180ms ease-out;
`;

const LogoLockupImg = styled.img`
  display: block;
  height: ${LOGO_H}px;
  width: ${LOGO_W}px;
  object-fit: contain;
  object-position: left center;
`;

const LinksCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
`;

/**
 * Section heading with a FIXED height so the rail does not shift vertically
 * between collapsed and expanded states: expanded shows the label text,
 * collapsed shows a short rule occupying the exact same height.
 */
const SectionHeadRow = styled.div`
  height: 26px;
  display: flex;
  align-items: center;
  padding: 0 4px;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const SectionHeadText = styled.span`
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.45;
  white-space: nowrap;
`;

const SectionHeadRule = styled.span`
  display: block;
  width: 20px;
  height: 2px;
  border-radius: 999px;
  background: currentColor;
  opacity: 0.18;
`;

const Avatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  object-fit: cover;
  flex-shrink: 0;
`;

// Small round gear at the right edge of the profile row; muted until the row is hovered.
const GearChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.bg === "#10182a" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"};
  transition: background 150ms, transform 150ms;

  && svg {
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.muted || "#8494a8"};
    transition: color 150ms, transform 300ms ease;
  }

  a:hover & {
    background: ${({ theme }) =>
      theme.bg === "#10182a" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)"};
  }

  a:hover && svg {
    color: ${({ theme }) => theme.ink || "rgba(255,255,255,0.92)"};
    transform: rotate(45deg);
  }
`;

const AvatarFallback = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Was branching on theme.bg === "#10182a" — a hex string compare that
     silently stops detecting dark mode the moment that value is retuned. A tint
     of --ink inverts on its own. */
  background: color-mix(in srgb, var(--ink) 12%, transparent);
  color: var(--ink);
  font-weight: 700;
  font-size: 0.75rem;
  flex-shrink: 0;
`;

function SidebarLogo() {
  const { open } = useSidebar();

  return (
    <LogoRow>
      <LogoClip $visible={open}>
        <LogoLockupImg
          src={publicAsset("scoutm8-landing-logo.png")}
          alt="ScoutM8"
        />
      </LogoClip>
    </LogoRow>
  );
}

/** Section heading: label when expanded, short rule when collapsed — same height. */
function SectionHead({ title }) {
  const { open } = useSidebar();
  return (
    <SectionHeadRow>
      {open ? (
        <SectionHeadText>{title}</SectionHeadText>
      ) : (
        <SectionHeadRule aria-hidden />
      )}
    </SectionHeadRow>
  );
}

const renderIcon = (Icon) => (
  <Icon style={{ width: 20, height: 20, flexShrink: 0 }} />
);

/**
 * Navigation grouped by the ScoutM8 information architecture.
 * Every entry is a flat row (icon + label) so the rail is structurally
 * identical whether collapsed or expanded — nothing shifts on hover.
 * Sub-views (Saved jobs, Tracked companies, a project's tabs) are handled
 * by in-page tabs, not extra sidebar rows.
 */
const NAV_SECTIONS = [
  {
    title: null,
    items: [
      { label: "Get started", href: "/get-started", icon: FiCompass },
      // The in-house edition opens on People instead; its sidebar has no Dashboard.
      { label: "Dashboard", href: "/", end: true, icon: FiBarChart2, agencyOnly: true },
    ],
  },
  {
    title: "Sourcing",
    items: [
      { label: "Jobs", href: "/jobs", icon: FiBriefcase, feature: "jobs" },
      {
        label: "People",
        href: "/find-candidates",
        icon: FiUserPlus,
      },
    ],
  },
  {
    title: "Pipeline",
    items: [
      { label: "Outreach", href: "/outreach", icon: FiMail, feature: "outreach" },
    ],
  },
  {
    title: "Intelligence",
    items: [
      { label: "Research", href: "/research", icon: DeviceSearch, feature: "research" },
    ],
  },
];

// Beside a section the plan doesn't include; the page itself explains and links to plans.
const NavLock = styled(FiLock)`
  margin-left: 8px;
  vertical-align: -1px;
  /* Doubled: the sidebar link recolours every svg inside it. */
  && {
    color: var(--secondary);
  }
`;

// Get started progress beside its sidebar label; gone once every step is done.
const GsCount = styled.span`
  margin-left: 8px;
  padding: 1px 6px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--accent);
  background: var(--accent-soft, transparent);
`;

function Sidebar() {
  const location = useLocation();
  const [uiOpen, setUiOpen] = useState(false);
  const { user, logout } = useAuth();
  const credits = useCredits();
  const { hasUnseen: hasUnseenFind } = useFindNotification();
  // A card-free trial shows its days left here. The shared plan state refreshes the moment a
  // purchase lands (the payment-received page signals it), so the badge clears without a reload.
  const { status: billing, features: planFeatures } = usePlan();
  const trialEndsAt = billing?.status === "TRIAL" && billing.trialEndsAt ? new Date(billing.trialEndsAt) : null;
  const trialDaysLeft = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt - Date.now()) / 86400000)) : null;
  // Profile label shows only the first name (from user_settings), falling
  // back to the first word of the Auth0 ID token's `name` claim when empty.
  const [settings, setSettings] = useState(() =>
    user?.sub ? getCachedUserSettings(user.sub) : null,
  );
  const [settingsLoaded, setSettingsLoaded] = useState(
    () => !!(user?.sub && getCachedUserSettings(user.sub)),
  );
  useEffect(() => {
    if (!user?.sub) return undefined;
    let alive = true;
    // Fetch on mount and refetch on cache invalidation so the name
    // shows immediately on refresh and updates after a profile save.
    const sync = () => {
      const cached = getCachedUserSettings(user.sub);
      if (cached) {
        if (alive) {
          setSettings(cached);
          setSettingsLoaded(true);
        }
        return;
      }
      getUserSettings(user.sub)
        .then((s) => {
          if (!alive) return;
          setSettings(s);
          setSettingsLoaded(true);
        })
        .catch(() => {});
    };
    sync();
    const unsubscribe = subscribeUserSettings(user.sub, sync);
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [user?.sub]);
  const getStarted = useGetStarted();
  const gsDone = getStarted ? openOnPlan(getStartedDone(getStarted, settings), planFeatures.plan === "trial", planFeatures.edition) : null;
  // In-house hides the agency side outright (no lock, no upgrade path): Dashboard and anything its edition mask turns off.
  const inhouse = isInhouse(planFeatures);
  const shown = (item) => !(inhouse && (item.agencyOnly || (item.feature && item.feature !== "outreach" && planFeatures[item.feature] === false)));
  const navSections = NAV_SECTIONS.map((sec) => ({ ...sec, items: sec.items.filter(shown) })).filter((sec) => sec.items.length > 0);
  const gsLeft = gsDone ? Object.values(gsDone).filter((v) => !v).length : null;
  const gsTotal = gsDone ? Object.keys(gsDone).length : null;
  // null → still resolving with no last-known name; render a skeleton
  // instead of flashing the Auth0 email while the fetch is in flight.
  const profileName = useMemo(() => {
    const first = String(settings?.firstName || "").trim();
    if (first) return first;
    if (settingsLoaded) return firstWord(user?.name) || "Profile";
    let stored = null;
    try {
      stored = window.localStorage.getItem(`profile-name:${user?.sub}`);
    } catch {}
    return firstWord(stored) || null;
  }, [settings, settingsLoaded, user?.sub, user?.name]);

  useEffect(() => {
    if (!user?.sub || !settings) return;
    const first = String(settings.firstName || "").trim();
    try {
      const key = `profile-name:${user.sub}`;
      if (first) window.localStorage.setItem(key, first);
      else window.localStorage.removeItem(key);
    } catch {}
  }, [settings, user?.sub]);

  // On small screens, close the mobile sidebar after navigation; keyed on location.key so a
  // same-path tap (Credits while on /settings, the current page's own link) still closes it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth <= 1024) {
      setUiOpen(false);
    }
  }, [location.key]);

  // When moving from mobile/tablet to desktop, keep desktop rail collapsed.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let wasMobile = window.innerWidth <= 1024;
    const handleResize = () => {
      const isMobile = window.innerWidth <= 1024;
      if (wasMobile && !isMobile) {
        setUiOpen(false);
      }
      wasMobile = isMobile;
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Shell>
      <SidebarRoot open={uiOpen} setOpen={setUiOpen} animate>
        <SidebarBody className="justify-between gap-3">
          <DesktopInner>
            <SidebarLogo />
            <LinksCol>
              {navSections.map((section, idx) => (
                <React.Fragment key={section.title || `section-${idx}`}>
                  {section.title && <SectionHead title={section.title} />}
                  {section.items.map((item) => (
                    <SidebarLink
                      key={item.href}
                      link={{
                        label:
                          item.href === "/get-started" && gsLeft && settingsLoaded ? (
                            <>
                              {item.label}
                              <GsCount>
                                {gsTotal - gsLeft}/{gsTotal}
                              </GsCount>
                            </>
                          ) : item.feature && !planFeatures[item.feature] ? (
                            <>
                              {item.label}
                              <NavLock size={12} aria-label="Not on your plan" />
                            </>
                          ) : (
                            item.label
                          ),
                        href: item.href,
                      end: item.end,
                      icon: renderIcon(item.icon),
                      activeMatch: item.activeMatch,
                      badge:
                        item.href === "/find-candidates" && hasUnseenFind,
                    }}
                    />
                  ))}
                </React.Fragment>
              ))}
            </LinksCol>
          </DesktopInner>
          {trialDaysLeft != null && (
            <SidebarLink
              link={{
                label: trialDaysLeft === 0 ? "Trial ends today" : `Trial: ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left`,
                href: "/checkout",
                icon: renderIcon(FiClock),
                activeMatch: () => false,
              }}
            />
          )}
          {/* Balance sits with the profile, not the nav: it's about the account, not a place. */}
          <SidebarLink
            link={{
              label:
                credits.balance == null ? (
                  <Skeleton width={70} height={12} inline />
                ) : (
                  `${formatCredits(credits.balance)} credits`
                ),
              href: "/settings?tab=credits",
              icon: renderIcon(FiCreditCard),
              activeMatch: () => false,
            }}
          />
          <SidebarLink
            link={{
              label: profileName ?? <Skeleton width={90} height={12} inline />,
              href: "/settings?tab=profile",
              trailing: (
                <GearChip title="Settings">
                  <FiSettings aria-label="Settings" />
                </GearChip>
              ),
              icon: user?.picture ? (
                <Avatar src={user.picture} alt="Profile avatar" />
              ) : (
                <AvatarFallback>
                  {(profileName || " ").slice(0, 1).toUpperCase()}
                </AvatarFallback>
              ),
            }}
          />
          <SidebarAction
            label="Log out"
            icon={
              <FiLogOut
                aria-hidden
                style={{ width: 20, height: 20, flexShrink: 0 }}
              />
            }
            onClick={logout}
          />
        </SidebarBody>
      </SidebarRoot>
    </Shell>
  );
}

export default Sidebar;
